import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MdrDashboardsGroupInterface } from '../../../interfaces/mdr-dashboards-group.interface';
import { DataResponse } from '../../../interfaces/mdr-dashboards.data-response.interface';
import { OrganizationsService } from '../../../../organizations/services/organizations.service';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';
import { zip } from 'rxjs';
import { ServiceStatusColorsEnum, ServiceStatusEnum } from '../../../enums/service-status.enum';
import { IdentityService } from '../../../../auth/services/identity.service';

@Component({
    selector: 'app-mdr-dashboards-group',
    templateUrl: './mdr-dashboards-group.component.html',
    styleUrls: ['./mdr-dashboards-group.component.scss'],
    standalone: false,
})
export class MdrDashboardsGroupComponent implements OnInit, OnDestroy {
    @Input() public element: MdrDashboardsGroupInterface;
    public chartsResponsesMap = new Map<string, DataResponse>();
    public isOnboarded: boolean = true;
    public emptyWidgetsWithChartsCount = 0;
    public emptyWidgetsWithoutChartsCount = 0;
    public countDataLoaded: number = 0;
    public incidentsData: any;
    public statusMonitoringData: any;
    public incidentsLoading: boolean;
    public statusLoading: boolean;
    public incidentsError: any;
    public statusServiceError: any;
    public detectChangesSub: Subscription;
    protected timeframeSubscription: Subscription;
    protected orgSubscription: Subscription;
    protected activityTrendData: DataResponse;

    constructor(
        private orgService: OrganizationsService,
        private timeframeService: TimeframeService,
        private cdr: ChangeDetectorRef,
        private mdrDashboardsService: MdrDashboardsService,
        private identityService: IdentityService,
    ) {}

    public get allInfoIsEmpty() {
        /* we remove the incidents from the widgets with charts since it comes in activity trend info*/
        return (
            this.emptyWidgetsWithoutChartsCount + (this.emptyWidgetsWithChartsCount - 1) ===
            this.getBasicWidgetsWithCharts().length + this.getBasicWidgetsWithoutCharts().length
        );
    }

    getMainWidgets() {
        return this.element.widgets.filter((widget) => widget.isMainWidget);
    }

    getBasicWidgetsWithCharts() {
        return this.element.widgets.filter((widget) => !widget.isMainWidget && widget.hasChart);
    }

    getBasicWidgetsWithoutCharts() {
        return this.element.widgets.filter((widget) => !widget.isMainWidget && !widget.hasChart);
    }

    getIncidentsTitlte(value: number | undefined) {
        return value === 0 ? 'NO INCIDENTS' : 'INCIDENTS';
    }

    public calculateSeriesTotal(series: any) {
        let sum = 0;

        if (series) {
            sum = series;
            sum = series.reduce((partialSum: number, a: any) => partialSum + a[1], 0);
        }

        return sum;
    }

    ngOnInit(): void {
        this.detectChangesSub = this.mdrDashboardsService.performDetectChanges.asObservable().subscribe((val) => {
            if (val) {
                this.countDataLoaded = 0;
                this.loadData();
            }
        });
    }

    public loadData() {
        this.incidentsData = null;
        this.statusMonitoringData = null;
        this.incidentsLoading = true;
        this.statusLoading = true;
        const getStatusData = this.getMainWidgets().find((x) => x.title.toLowerCase() === 'service status')?.data;
        /* Load status monitoring data */
        if (getStatusData) {
            getStatusData.subscribe(
                (status) => {
                    this.statusMonitoringData = status;
                    this.isOnboarded = this.statusMonitoringData.service_status !== ServiceStatusEnum.ONBOARDING;
                    this.statusLoading = false;
                },
                (error) => {
                    this.statusServiceError = error;
                    this.statusLoading = false;
                },
            );
        }

        this.chartsResponsesMap.clear();

        const currentTimeframe = this.timeframeService.currentlySelectedTimeframe();
        const org = this.orgService.currentlySelectedOrganization() || this.identityService.company;
        /* Make the activity trend call to retrieve : hunts, investigations for spike charts */
        this.mdrDashboardsService.getWidgetData(currentTimeframe?.name, '/activity-trend', org).subscribe(
            (val) => {
                this.activityTrendData = val;
                this.emptyWidgetsWithChartsCount = 0;
                this.incidentsData = {
                    value: this.calculateSeriesTotal(
                        this.activityTrendData.data.find((x: any) => x.name.toLowerCase() === 'incidents').data,
                    ),
                };
                this.incidentsLoading = false;
                this.incidentsError = null;
                for (let i = 0; i < this.activityTrendData.data.length; i++) {
                    this.chartsResponsesMap.set(this.activityTrendData.data[i].name, this.activityTrendData.data[i]);
                    if (this.calculateSeriesTotal(this.activityTrendData.data[i].data) === 0) {
                        this.emptyWidgetsWithChartsCount = this.emptyWidgetsWithChartsCount + 1;
                    }
                }
            },
            (error) => {
                this.incidentsLoading = false;
                this.incidentsError = error;
            },
        );
        /* Load data in line without charts like response actions and recommendations*/
        zip(this.getBasicWidgetsWithoutCharts().map((x) => x.data)).subscribe((responses) => {
            this.emptyWidgetsWithoutChartsCount = 0;
            for (let i = 0; i < responses.length; i++) {
                this.chartsResponsesMap.set(this.getBasicWidgetsWithoutCharts()[i].title, responses[i]);
                if (responses[i].data.value === 0) {
                    this.emptyWidgetsWithoutChartsCount = this.emptyWidgetsWithoutChartsCount + 1;
                }
            }

            this.cdr.detectChanges();
        });
    }

    public getValueFromResponseMap(title: string) {
        return this.chartsResponsesMap.get(title);
    }

    ngOnDestroy(): void {
        if (this.orgSubscription) {
            this.orgSubscription.unsubscribe();
        }

        if (this.timeframeSubscription) {
            this.timeframeSubscription.unsubscribe();
        }

        if (this.detectChangesSub) {
            this.detectChangesSub.unsubscribe();
        }
    }

    protected getTextFromEnumId() {
        switch ((this.statusMonitoringData as any).service_status) {
            case 1:
                return 'ONBOARDING';
                break;
            case 2:
                return 'MONITORED';
                break;
            case 3:
                return 'OFFBOARDING';
                break;
            default:
                return 'ONBOARDING';
        }
    }

    protected getServiceColor() {
        return ServiceStatusColorsEnum[this.getTextFromEnumId()];
    }
}
